import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { usePortfolio } from "../../context/PortfolioContext";
import { fmtINR } from "../../lib/format";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { Plus, Trash2, Mail, Shield, UserPlus, Users, Pencil, Lock, Wand2 } from "lucide-react";

const PRIMARY_ADMIN_EMAIL = "portfolio.daasmin@gmail.com";

export default function ManageMembers() {
  const { toast } = useToast();
  const { members, reload } = usePortfolio();
  const [tab, setTab] = useState("members");
  const [gmails, setGmails] = useState([]);
  const [loadingG, setLoadingG] = useState(false);

  const [memberForm, setMemberForm] = useState({
    name: "",
    openingCapital: "",
    openingUnits: "",
    openingNav: "",
  });

  const [gmailForm, setGmailForm] = useState({
    email: "",
    memberId: "",
    role: "member",
  });

  const [editingMember, setEditingMember] = useState(null);
  const [editingGmail, setEditingGmail] = useState(null);

  const loadGmails = async () => {
    setLoadingG(true);
    try {
      const list = await api.listAuthorizedGmails();
      setGmails(list);
    } catch (e) {}
    setLoadingG(false);
  };

  useEffect(() => {
    loadGmails();
  }, []);

  const primaryAdminMemberId = gmails.find((g) => g.email === PRIMARY_ADMIN_EMAIL)?.memberId;

  const autoUnits = () => {
    const cap = parseFloat(memberForm.openingCapital);
    const nav = parseFloat(memberForm.openingNav);
    if (!isNaN(cap) && !isNaN(nav) && nav > 0) {
      setMemberForm((f) => ({ ...f, openingUnits: (cap / nav).toFixed(4) }));
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!memberForm.name.trim()) return toast({ title: "Name required" });
    try {
      await api.addMember({
        name: memberForm.name.trim(),
        openingCapital: parseFloat(memberForm.openingCapital) || 0,
        openingUnits: parseFloat(memberForm.openingUnits) || 0,
        openingNav: parseFloat(memberForm.openingNav) || 0,
      });
      toast({ title: "Member added", description: memberForm.name });
      setMemberForm({ name: "", openingCapital: "", openingUnits: "", openingNav: "" });
      await reload();
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail });
    }
  };

  const saveMemberEdit = async () => {
    if (!editingMember || !editingMember.name.trim()) return toast({ title: "Name required" });
    try {
      await api.editMember(editingMember.id, { name: editingMember.name.trim() });
      toast({ title: "Member updated" });
      setEditingMember(null);
      await reload();
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail });
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(
      `Remove "${m.name}"? This will delete their transactions and any linked Gmail access. Balances & NAV will be recomputed. This cannot be undone.`
    )) return;
    try {
      await api.removeMember(m.id);
      toast({ title: "Member removed", description: m.name });
      await Promise.all([reload(), loadGmails()]);
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail });
    }
  };

  const [cleaning, setCleaning] = useState(false);
  const cleanupDuplicates = async () => {
    if (!window.confirm(
      "Scan for duplicate-named members and orphan (0-balance, 0-transaction) members? Duplicates will be merged into the entry with the most data. This cannot be undone."
    )) return;
    setCleaning(true);
    try {
      const r = await api.cleanupDuplicateMembers();
      const merged = r.merged || 0;
      const orphans = r.orphans_removed || 0;
      if (merged === 0 && orphans === 0) {
        toast({ title: "All clean", description: "No duplicates or orphan members found." });
      } else {
        toast({
          title: "Cleanup complete",
          description: `Merged ${merged} duplicate(s), removed ${orphans} orphan(s).`,
        });
      }
      await Promise.all([reload(), loadGmails()]);
    } catch (e) {
      toast({ title: "Cleanup failed", description: e.response?.data?.detail });
    }
    setCleaning(false);
  };

  const addGmail = async (e) => {
    e.preventDefault();
    if (!gmailForm.email.includes("@")) return toast({ title: "Invalid email" });
    if (!gmailForm.memberId) return toast({ title: "Select member" });
    try {
      await api.addAuthorizedGmail(gmailForm);
      toast({ title: "Gmail authorized", description: gmailForm.email });
      setGmailForm({ email: "", memberId: "", role: "member" });
      await loadGmails();
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail });
    }
  };

  const saveGmailEdit = async () => {
    if (!editingGmail) return;
    try {
      await api.editAuthorizedGmail(editingGmail.email, {
        memberId: editingGmail.memberId,
        role: editingGmail.role,
      });
      toast({ title: "Gmail updated" });
      setEditingGmail(null);
      await loadGmails();
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail });
    }
  };

  const revokeGmail = async (email) => {
    if (!window.confirm(`Revoke access for ${email}?`)) return;
    try {
      await api.revokeAuthorizedGmail(email);
      await loadGmails();
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail });
    }
  };

  return (
    <div>
      <div className="inline-flex rounded-md border border-slate-800 overflow-hidden mb-5">
        <button
          onClick={() => setTab("members")}
          data-testid="members-tab"
          className={`flex items-center gap-2 px-4 py-2 text-xs ${
            tab === "members" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-white"
          }`}
        >
          <Users size={14} /> Family Members
        </button>
        <button
          onClick={() => setTab("gmails")}
          data-testid="gmails-tab"
          className={`flex items-center gap-2 px-4 py-2 text-xs border-l border-slate-800 ${
            tab === "gmails" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-white"
          }`}
        >
          <Mail size={14} /> Gmail Access
        </button>
      </div>

      {tab === "members" && (
        <div className="grid lg:grid-cols-5 gap-5">
          <form onSubmit={addMember} className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6 h-fit">
            <div className="flex items-center gap-2">
              <UserPlus size={16} className="text-amber-400" />
              <div className="text-sm font-semibold text-white">Add Family Member</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Creates a new member and records opening transaction.
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Full Name</Label>
                <Input
                  value={memberForm.name}
                  data-testid="new-member-name"
                  onChange={(e) => setMemberForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Zara Chamadia"
                  className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Opening Capital ₹</Label>
                <Input
                  type="number" step="0.01"
                  value={memberForm.openingCapital}
                  onChange={(e) => setMemberForm((f) => ({ ...f, openingCapital: e.target.value }))}
                  onBlur={autoUnits}
                  placeholder="e.g. 100000"
                  className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Opening NAV ₹</Label>
                <Input
                  type="number" step="0.0001"
                  value={memberForm.openingNav}
                  onChange={(e) => setMemberForm((f) => ({ ...f, openingNav: e.target.value }))}
                  onBlur={autoUnits}
                  placeholder="e.g. 105.1500"
                  className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Opening Units (auto)</Label>
                <Input
                  type="number" step="0.0001"
                  value={memberForm.openingUnits}
                  onChange={(e) => setMemberForm((f) => ({ ...f, openingUnits: e.target.value }))}
                  placeholder="Capital ÷ NAV"
                  className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500 font-mono"
                />
              </div>
            </div>
            <Button type="submit" data-testid="add-member-submit" className="mt-5 w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              <Plus size={16} className="mr-2" /> Add member
            </Button>
          </form>

          <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-white">All Family Members</div>
                <div className="text-xs text-slate-500">{members.length} members</div>
              </div>
              <button
                onClick={cleanupDuplicates}
                disabled={cleaning}
                data-testid="cleanup-duplicates-btn"
                title="Merge duplicate-named members & remove orphan (empty) members"
                className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border border-slate-700 bg-slate-950 text-slate-300 hover:bg-slate-800 hover:text-amber-400 hover:border-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Wand2 size={12} />
                {cleaning ? "Cleaning…" : "Cleanup duplicates"}
              </button>
            </div>
            <div className="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
              {members.map((m) => {
                const isProtected = primaryAdminMemberId === m.id;
                return (
                  <div key={m.id} className="px-5 py-3 flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 shrink-0 font-medium text-sm">
                      {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-2">
                        {m.name}
                        {isProtected && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                            <Lock size={9} /> Admin-linked
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {m.units > 0 ? `${m.units.toFixed(4)} units · ${fmtINR(m.capitalInvested)}` : "No units"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setEditingMember({ id: m.id, name: m.name })}
                        data-testid={`edit-member-${m.id}`}
                        className="text-slate-500 hover:text-emerald-400 p-2"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      {!isProtected && (
                        <button
                          onClick={() => removeMember(m)}
                          data-testid={`delete-member-${m.id}`}
                          className="text-slate-500 hover:text-rose-400 p-2"
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "gmails" && (
        <div className="grid lg:grid-cols-5 gap-5">
          <form onSubmit={addGmail} className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6 h-fit">
            <div className="flex items-center gap-2">
              <Plus size={16} className="text-amber-400" />
              <div className="text-sm font-semibold text-white">Authorize Gmail</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">Only emails here can sign in with Google.</div>

            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Gmail address</Label>
                <Input type="email" value={gmailForm.email}
                  data-testid="new-gmail-email"
                  onChange={(e) => setGmailForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="member@gmail.com"
                  className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500"/>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Link to member</Label>
                <Select value={gmailForm.memberId} onValueChange={(v) => setGmailForm((f) => ({ ...f, memberId: v }))}>
                  <SelectTrigger data-testid="new-gmail-member" className="mt-1.5 bg-slate-950 border-slate-800 text-white focus:ring-emerald-500">
                    <SelectValue placeholder="Select family member" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-[280px]">
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Role</Label>
                <div className="mt-2 inline-flex rounded-md border border-slate-800 overflow-hidden">
                  <button type="button" onClick={() => setGmailForm((f) => ({ ...f, role: "member" }))}
                    className={`px-4 py-2 text-xs ${gmailForm.role === "member" ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-white"}`}>Member</button>
                  <button type="button" onClick={() => setGmailForm((f) => ({ ...f, role: "admin" }))}
                    className={`px-4 py-2 text-xs border-l border-slate-800 ${gmailForm.role === "admin" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-white"}`}>Admin</button>
                </div>
              </div>
            </div>
            <Button type="submit" data-testid="add-gmail-submit" className="mt-5 w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              <Plus size={16} className="mr-2" /> Grant access
            </Button>
          </form>

          <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-800">
              <div className="text-sm font-semibold text-white">Authorized Gmails</div>
              <div className="text-xs text-slate-500">{gmails.length} authorized</div>
            </div>
            <div className="divide-y divide-slate-800">
              {loadingG && <div className="text-xs text-slate-500 p-5">Loading…</div>}
              {gmails.map((row) => {
                const mem = members.find((m) => m.id === row.memberId);
                const isPrimary = row.email === PRIMARY_ADMIN_EMAIL;
                return (
                  <div key={row.email} className="px-5 py-3 flex items-center gap-3 group">
                    <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 shrink-0">
                      {row.role === "admin" ? <Shield size={14} className="text-amber-400" /> : <Mail size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-2">
                        {row.email}
                        {isPrimary && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                            <Lock size={9} /> Primary
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {mem?.name || "Unlinked"}
                        {row.role === "admin" && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Admin</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!isPrimary && (
                        <>
                          <button
                            onClick={() => setEditingGmail({ email: row.email, memberId: row.memberId || "", role: row.role || "member" })}
                            data-testid={`edit-gmail-${row.email}`}
                            className="text-slate-500 hover:text-emerald-400 p-2"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => revokeGmail(row.email)}
                            data-testid={`revoke-gmail-${row.email}`}
                            className="text-slate-500 hover:text-rose-400 p-2"
                            title="Revoke"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Edit Member dialog */}
      <Dialog open={!!editingMember} onOpenChange={(o) => !o && setEditingMember(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit member</DialogTitle>
          </DialogHeader>
          {editingMember && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Full Name</Label>
                <Input
                  value={editingMember.name}
                  data-testid="edit-member-name-input"
                  onChange={(e) => setEditingMember((m) => ({ ...m, name: e.target.value }))}
                  className="mt-1.5 bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingMember(null)} className="text-slate-400 hover:text-white hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={saveMemberEdit} data-testid="save-member-edit" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Gmail dialog */}
      <Dialog open={!!editingGmail} onOpenChange={(o) => !o && setEditingGmail(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Gmail access</DialogTitle>
          </DialogHeader>
          {editingGmail && (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                Email: <span className="text-white">{editingGmail.email}</span>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Linked member</Label>
                <Select value={editingGmail.memberId} onValueChange={(v) => setEditingGmail((g) => ({ ...g, memberId: v }))}>
                  <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-800 text-white focus:ring-emerald-500">
                    <SelectValue placeholder="Select member" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-[280px]">
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Role</Label>
                <div className="mt-2 inline-flex rounded-md border border-slate-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setEditingGmail((g) => ({ ...g, role: "member" }))}
                    className={`px-4 py-2 text-xs ${editingGmail.role === "member" ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-400"}`}
                  >
                    Member
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingGmail((g) => ({ ...g, role: "admin" }))}
                    className={`px-4 py-2 text-xs border-l border-slate-800 ${editingGmail.role === "admin" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400"}`}
                  >
                    Admin
                  </button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingGmail(null)} className="text-slate-400 hover:text-white hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={saveGmailEdit} data-testid="save-gmail-edit" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
